import './style.modules.scss'

import { Spacer } from '@ampcore/adel-react'
import { SpacerSpacing } from '@ampcore/adel-react'
import {
  BrandIcon,
  BrandIconColor,
  BrandIconId,
  MaterialIcon
} from '@ampcore/adel-react'
import {
  AmpcoreMaterialIconId,
  BackgroundColor
} from '@ampcore/ampcom-contracts'
import {
  AdvisorDataService,
  EmailPayloadTemplate,
  FaceProfileData
} from '@ampcore/client-side-services'
import { sanitizeUrl } from '@ampcore/string-format'
import { AnalyticsTagBuilder } from '@dai-team/analytics-library'
import Cookies from 'js-cookie'
import React, { useEffect, useMemo, useState } from 'react'
import xss from 'xss'

import { useAmpUserStatus } from '../../hooks/useAmpUserStatus'
import { usePageHeading } from '../../hooks/usePageHeading'
import { usePageTitle } from '../../hooks/usePageTitle'
import { EmailModal } from '../../types/EmailModal.type'
import { UserStatus } from '../../types/UserStatus.enum'
import { EmailAdvisorModal } from '../EmailAdvisorModal/EmailAdvisorModal'
import { ctaFilterXss } from './cta-filter-xss'
import { CtaBackgroundColor } from './CtaBgColor.type'

export type CtaProps = {
  componentSpacing?: SpacerSpacing
  body?: string
  bgColor?: CtaBackgroundColor
  headline?: string
  heading?: string
  linkHref?: string
  linkTarget?: string
  icon?: BrandIconId
  iconColor?: BrandIconColor
  fill?: boolean
  stroke?: boolean
  analytics?: string
  variant?: string
  analyticsName?: string
  psnAnalytics?: string
  testId?: string
}

export type CtaWithEmailProps = {
  prospect?: CtaProps
  client?: CtaProps
  variant?: string
  emailModal?: EmailModal
  componentSpacing?: SpacerSpacing
}

export type CtaCombinedProps = CtaProps | CtaWithEmailProps

export const Cta = (props: CtaCombinedProps) => {
  const [activeProps, setActiveProps] = useState<CtaProps>({})
  const [isHover, setIsHover] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const faceProfileCookie = Cookies.get('FaceProfileCookie')
  const userStatus = useAmpUserStatus()
  const [advisorData, setAdvisorData] = useState<FaceProfileData | undefined>()
  const pageHeading = usePageHeading()
  const pageTitle = usePageTitle()

  useEffect(() => {
    if (
      props.variant === 'cta-with-email' &&
      ('prospect' in props || 'client' in props)
    ) {
      setActiveProps(props.prospect as CtaWithEmailProps)
      if (
        faceProfileCookie &&
        (userStatus == UserStatus.CLIENT_ACTIVE ||
          userStatus == UserStatus.CLIENT_EXPIRED)
      ) {
        const fetchData = async () => {
          const advisorDataService = new AdvisorDataService()
          const faceProfileResponse =
            await advisorDataService.getById(faceProfileCookie)
          setAdvisorData(faceProfileResponse.data ?? undefined)
        }
        fetchData()
        setActiveProps(props.client as CtaWithEmailProps)
      }
    } else {
      setActiveProps(props as CtaProps)
    }
  }, [props, faceProfileCookie, userStatus])

  const {
    componentSpacing,
    body,
    bgColor,
    headline,
    heading,
    linkHref,
    linkTarget,
    icon,
    iconColor,
    fill,
    stroke,
    analytics,
    analyticsName,
    psnAnalytics,
    testId
  } = activeProps

  const { emailModal } = activeProps as CtaWithEmailProps

  const atb = useMemo(
    () =>
      new AnalyticsTagBuilder()
        .setDefaultName('Cta')
        .setAnalyticsName(analyticsName)
        .setPsnAnalytics(psnAnalytics),
    [analyticsName, psnAnalytics]
  )

  const emailMessage = () => {
    if (!emailModal?.emailBody) {
      return ''
    }

    const placeholderLinkText = (pageHeading || pageTitle || '').trim()

    const isPlainText = !/<[a-z][\s\S]*>/i.test(emailModal?.emailBody)
    let message = emailModal?.emailBody
    if (isPlainText) {
      message = message?.replace(/(\r\n|\n|\r)/g, '<br/>')
    }
    return message?.replace(
      '%article%',
      `<a href="${window.location.href}" target="_blank" rel="noopener noreferrer">${placeholderLinkText}</a>`
    )
  }

  const getLinkHref = () => {
    if (linkHref?.includes('ADVISOR_REFERRAL') && advisorData) {
      const advisorReferUrl =
        advisorData.type === 'team'
          ? advisorData.urls.site
          : advisorData.urls.referme
      return advisorReferUrl
    }
    return linkHref
  }

  const handleDarken = () => {
    setIsHover(true)
  }

  const handleLighten = () => {
    setIsHover(false)
  }

  const handleOpenModal = () => {
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
  }

  let bgColorClass = ''
  switch (bgColor) {
    case BackgroundColor.PURPLE:
    case BackgroundColor.PURPLE_TINT:
      bgColorClass = 'u-bgColorPurpleLight10'
      break
    case BackgroundColor.LIME:
    case BackgroundColor.LIME_TINT:
      bgColorClass = 'u-bgColorLimeLight20'
      break
    case BackgroundColor.MINT:
    case BackgroundColor.MINT_TINT:
      bgColorClass = 'u-bgColorMintLight10'
      break
    case BackgroundColor.BERRY_TINT:
      bgColorClass = 'u-bgColorBerryLight10'
      break
    case BackgroundColor.ORANGE_TINT:
      bgColorClass = 'u-bgColorOrangeLight20'
      break
    default:
      bgColorClass = 'u-bgColorStone'
  }

  return (
    <Spacer spacing={componentSpacing}>
      <div className="u-sizeConstrained">
        <div
          className={`Cta u-posRelative ${bgColorClass}`}
          data-testid={testId ? `${testId}` : null}
        >
          {props.variant === 'cta-with-email' &&
          advisorData?.email &&
          !linkHref?.includes('ADVISOR_REFERRAL') ? (
            <>
              <div className={`Cta-background ${isHover ? 'is-active' : ''}`}>
                {/* eslint-disable-next-line jsx-a11y/anchor-has-content, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                <a
                  className="Link--stretched"
                  onClick={handleOpenModal}
                  onMouseEnter={handleDarken}
                  onMouseLeave={handleLighten}
                  onFocus={handleDarken}
                  onBlur={handleLighten}
                  data-testid={testId ? `${testId}-button` : null}
                  {...(() => {
                    const attributes = atb.dataAnalyticAttribute(
                      'email-advisor-button'
                    )
                    return attributes
                  })()}
                />
              </div>
              <EmailAdvisorModal
                isModalOpen={isModalOpen}
                onClose={closeModal}
                advisorInfo={{
                  name: advisorData?.name ?? '',
                  url: advisorData?.urls?.site
                }}
                payload={{
                  subject: emailModal?.emailSubject ?? '',
                  greeting: emailModal?.emailGreeting ?? '',
                  message: emailMessage() ?? '',
                  recipient: advisorData?.email,
                  template: EmailPayloadTemplate.CLIENT_INQUIRY,
                  overrideRecipient: emailModal?.overrideRecipient
                }}
                disclosureText={emailModal?.disclosureText}
              />
            </>
          ) : (
            linkHref && (
              <div className={`Cta-background ${isHover ? 'is-active' : ''}`}>
                <a
                  className="Link--stretched"
                  href={sanitizeUrl(xss(getLinkHref() ?? ''))}
                  target={linkTarget}
                  aria-label={heading}
                  onMouseEnter={handleDarken}
                  onMouseLeave={handleLighten}
                  onFocus={handleDarken}
                  onBlur={handleLighten}
                  data-analytics={analytics || 'cta-component-link'}
                  data-testid={testId ? `${testId}-link` : null}
                >
                  <span className="u-hidden">{heading}</span>
                </a>
              </div>
            )
          )}
          <div className="Cta-wrapper u-lg-flex u-md-flex">
            {icon && (
              <div
                className="Cta-icon u-flex u-flexAlignItemsCenter"
                data-testid={testId ? `${testId}-icon` : null}
              >
                <BrandIcon
                  icon={icon}
                  color={iconColor ? iconColor : BrandIconColor.NONE}
                  fill={fill ? true : false}
                  stroke={stroke ? true : false}
                />
              </div>
            )}
            <div className="Cta-body u-lg-size3of4 u-md-size3of4">
              {headline && (
                <h5
                  className="Cta-superHeadline u-marginBottom20 u-textColorNeutralGrey u-textUppercase"
                  data-testid={testId ? `${testId}-super-headline` : null}
                  dangerouslySetInnerHTML={{ __html: xss(headline) }}
                ></h5>
              )}
              {heading && (
                <h3
                  className="Cta-heading u-marginBottom8 u-textColorNeutralGrey"
                  data-testid={testId ? `${testId}-heading` : null}
                  dangerouslySetInnerHTML={{ __html: xss(heading) }}
                ></h3>
              )}
              {body && (
                <div
                  className="Cta-content Content u-textColorNeutralGrey"
                  dangerouslySetInnerHTML={{
                    __html: ctaFilterXss.process(body)
                  }}
                  data-testid={testId ? `${testId}-body` : null}
                ></div>
              )}
            </div>
            <div className="Cta-link u-size1of4 u-flex u-lg-flexJustifyEnd u-md-flexJustifyEnd u-flexAlignItemsCenter">
              <MaterialIcon
                icon={AmpcoreMaterialIconId.CHEVRON_RIGHT}
                className={`u-flex u-flexAlignItemsCenter u-flexJustifyCenter u-textColorNeutralGrey ${
                  isHover ? 'is-active' : ''
                }`}
                ariaLabel="Slide Right"
              />
            </div>
          </div>
        </div>
      </div>
    </Spacer>
  )
}
